import type { ScheduleModel } from '@/shared/generated'

import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { any, prop } from 'lodash/fp'
import { ClockIcon, ExternalLinkIcon, Trash2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import {
	formatSchedule,
	useDeleteScheduleMutation,
	useSchedulesQuery,
} from '@/entities/optimization-schedule'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import NoData from '@/shared/components/placeholders/NoData'
import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import { LinkButton } from '@/shared/components/ui/link-button'
import { Loader } from '@/shared/components/ui/loader'
import {
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Separator } from '@/shared/components/ui/separator'
import {
	Table,
	TableBody,
	TableCell,
	TableRow,
} from '@/shared/components/ui/table'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'

type SchedulesListProps = {
	close(): void
	isOpen: boolean
}

export function OptimizationSchedulesModal({
	close,
	isOpen,
}: SchedulesListProps) {
	const { t } = useTranslation()

	const navigate = useNavigate()
	const { showModal } = useModals()
	const deleteSchedule = useDeleteScheduleMutation()
	const optimizationGroups = useOptimizationGroupsQuery()
	const schedules = useSchedulesQuery()
	const isLoading = any(prop('isLoading'), [optimizationGroups, schedules])
	const closeModal = () => close()
	const handleDeleteClick = async ({
		name,
		id,
	}: {
		name: string
		id: string
	}) => {
		const answer = await confirm({
			title: t('general_detete_confirm_title'),
			description: t('general_detete_confirm_subtitle'),
			actionText: t('general_delete', {
				ns: 'keys',
			}),
			cancelText: t('general_cancel', {
				ns: 'keys',
			}),
			ActionProps: {
				variant: 'destructive',
			},
		})
		if (answer) {
			ampli.autorunDeleteSchedule({
				'configuration name': name,
			})
			await deleteSchedule.mutateAsync(id)
			closeModal()
		}
	}

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent className='max-w-3xl'>
				<ErrorBoundary>
					<ModalHeader>
						<ModalTitle>{t('schedule_configurations')}</ModalTitle>
					</ModalHeader>
					<ModalBody className='min-h-96'>
						{isLoading && <Loader />}
						<div className='border-base relative flex flex-col overflow-hidden rounded-lg border border-solid text-sm'>
							<Table className='table-fixed overflow-hidden'>
								<TableBody>
									{schedules.data?.length ? (
										schedules.data?.map((schedule: ScheduleModel) => {
											const handleNavigate = () =>
												showModal('OPTIMIZATION_SCHEDULE_MODAL', {
													id: schedule.id,
												})

											return (
												<TableRow key={schedule.id} className='h-12'>
													<TableCell className='flex items-center gap-3 truncate px-3 py-2'>
														<ClockIcon className='size-5 shrink-0 text-muted-foreground' />
														<div className='truncate'>
															<div className='font-medium'>
																<LinkButton onClick={handleNavigate}>
																	{schedule.name}
																</LinkButton>
															</div>
															<div className='inline-flex h-full items-center gap-2 truncate text-sm text-muted-foreground'>
																<span>
																	{schedule.optimization_groups?.length} OGs
																</span>
																<Separator
																	orientation='vertical'
																	className='h-4!'
																/>
																<span className='truncate'>
																	{formatSchedule(schedule, t)}
																</span>
															</div>
														</div>
													</TableCell>
													<TableCell className='w-24 px-3 py-2 text-right'>
														<Button
															variant='ghost'
															size='icon'
															onClick={(e) => {
																e.stopPropagation()
																navigate({
																	to: `?schedule.id__array_any_of=${schedule.id}`,
																})

																closeModal()
															}}
														>
															<ExternalLinkIcon />
														</Button>
														<Button
															variant='ghost'
															size='icon'
															onClick={(e) => {
																e.stopPropagation()
																handleDeleteClick(schedule)
															}}
														>
															<Trash2Icon />
														</Button>
													</TableCell>
												</TableRow>
											)
										})
									) : (
										<NoData />
									)}
								</TableBody>
							</Table>
						</div>
					</ModalBody>
				</ErrorBoundary>
			</ModalContent>
		</Modal>
	)
}
